<template>
  <div class="signature-container" :data-testid="testId">
    <!-- Checkbox (если нужен) -->
    <div v-if="checkboxLabel" class="signature-checkbox" :data-testid="checkboxWrapperTestId">
      <a-checkbox
        v-if="!signature?.signed_date"
        v-model="isChecked"
        :data-testid="checkboxTestId"
        @change="emitChecked"
      >
        {{ checkboxLabel }}
      </a-checkbox>
      <span v-else>{{ checkboxLabel }}</span>
    </div>

    <!-- Signature Area -->
    <div class="signature-content">
      <div class="flex justify-content-between">
        <span class="associate-name">{{ name }}</span>

        <div v-if="signature?.signed_date" class="right-upper-corner">
          <div><b>E-Signed:</b></div>
          <div>
            <b>{{ visualSignatureDateAndTime }}</b>
          </div>
        </div>
      </div>

      <signature-canvas
        ref="signaturePad"
        :image-url="signature?.image_url"
        :signed-date="signature?.signed_date"
        pen-color="#000000"
        background-color="#f4f4f4"
        :test-id="canvasContainerTestId"
        :canvas-element-test-id="canvasElementTestId"
        :clear-button-test-id="clearButtonTestId"
        :undo-button-test-id="undoButtonTestId"
      />

      <div class="signature-footer">
        <span class="associate-email">{{ signature?.email ?? currentAssociate?.email }}</span>
        <span class="associate-phone">{{ signature?.phone ?? currentAssociate?.phone }}</span>
      </div>
    </div>
  </div>
</template>

<script>
import moment from 'moment-timezone'
import { Checkbox, notification } from 'ant-design-vue'
import SignatureCanvas from './signature-canvas.vue'
import { mapActions } from 'vuex'
import { deepCopy } from '@/utils/deep-copy'
import util from '@/util'

export default {
  name: 'SignatureComponent',
  components: {
    'a-checkbox': Checkbox,
    'signature-canvas': SignatureCanvas
  },
  emits: ['save-signature', 'checked'],
  props: {
    checkboxLabel: { type: String, default: null },
    availableSignature: { type: Object, default: null },
    testId: { type: String, default: undefined },
    checkboxWrapperTestId: { type: String, default: undefined },
    checkboxTestId: { type: String, default: undefined },
    canvasContainerTestId: { type: String, default: undefined },
    canvasElementTestId: { type: String, default: undefined },
    clearButtonTestId: { type: String, default: undefined },
    undoButtonTestId: { type: String, default: undefined }
  },
  data() {
    return {
      isChecked: false,
      isDefaultSignature: false,
      isConfirmVisible: false,
      signature: undefined,
      sigOption: {
        penColor: 'rgb(0, 0, 0)',
        backgroundColor: '#f4f4f4'
      }
    }
  },
  watch: {
    availableSignature: {
      handler(newVal) {
        this.signature = deepCopy(newVal)
      },
      immediate: true
    }
  },
  computed: {
    dateTimeFormat() {
      return this.$store.state.applicationState.dateTimeFormat
    },
    visualSignatureDateAndTime() {
      if (!this.signature?.signed_date) return ''
      return moment(this.signature?.signed_date).format(this.dateTimeFormat)
    },
    currentAssociate() {
      return this.$store.state.applicationState.associate
    },
    name() {
      if (this.signature && this.signature?.first_name && this.signature?.last_name) {
        return util.combineAssociateName(this.signature.first_name, this.signature.last_name)
      }

      return util.combineAssociateName(this.currentAssociate.first_name, this.currentAssociate.last_name)
    }
  },
  methods: {
    ...mapActions(['saveSignatureContext', 'uploadFile']),

    resetSignature() {
      this.signature = null
    },

    emitChecked() {
      this.$emit('checked', this.isChecked)
    },

    async saveSignature() {
      const result = this.$refs.signaturePad.getDataURL()

      if (result.isEmpty) {
        notification.warning({
          message: 'Signature is empty',
          description: 'Please draw your signature.'
        })
        return
      }

      await this.uploadSignature(result.url)
    },

    async uploadSignature(dataUrl) {
      try {
        const file = await this.dataUrlToFile(dataUrl, 'sign')

        const data = await this.uploadFile({ files: [file] })

        if (this.checkboxLabel) {
          const signatureData = await this.saveSignatureContext({
            signature: data[0]
          })

          this.signature = deepCopy(signatureData.signature)

          this.$emit('save-signature', signatureData.signature.id)
        } else {
          this.$emit('save-signature', data[0])
        }
      } catch (error) {
        this.resetSignature()

        notification.error({
          message: 'Upload Error',
          description: typeof error === 'string' ? error : error.message
        })
      }
    },

    async dataUrlToFile(dataUrl, filename) {
      const res = await fetch(dataUrl)
      const blob = await res.blob()
      return new File([blob], filename, { type: 'image/png' })
    }
  },

  mounted() {
    this.emitChecked()
  }
}
</script>

<style scoped>
.signature-container {
  display: flex;
  flex-direction: column;
  gap: 16px;
}

.signature-content {
  border: 1px solid var(--border-color-base);
  border-radius: 10px;
  padding: 16px;
  background: var(--bg-component);
}

.associate-name {
  font-weight: bold;
  font-size: 14px;
}

.right-upper-corner {
  text-align: right;
  font-size: 14px;
}

.signature-footer {
  display: flex;
  justify-content: space-between;
  gap: 10px;
  margin-top: 8px;
}

.associate-email,
.associate-phone {
  font-size: 12px;
  word-break: break-all;
  min-width: 100px;
}

.associate-phone {
  text-align: right;
}

.required-field {
  color: red;
}
</style>
